import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Play, Square, XCircle, UserX, Clock,
  LogIn, LogOut, CalendarDays, CalendarClock, AlertTriangle, MapPin, Wallet, ClipboardCheck,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import RescheduleModal from '../components/RescheduleModal';
import CancelSessionModal from '../components/CancelSessionModal';
import { dayKey, addDaysToKey } from '../lib/practiceTime';
import { t, formatTime, formatDate, formatMoney, formatDuration, ageAt } from '../i18n';
import {
  STATUS, STATUS_TONE, changeStatus, setBillable, fetchDayAppointments,
  fetchDayDurations, groupDay, fetchOpenAttendance, checkInProfessional,
  checkOutProfessional,
} from '../lib/appointments';
import {
  Card, Button, Badge, Alert, Spinner, EmptyState, Select,
} from '../components/ui';

/**
 * The professional's day: who is in the room now, who is waiting, who is still
 * to come, and what already happened.
 *
 * Waiting normally arrives on its own from a QR scan; the manual control is
 * here for when a patient arrives without scanning.
 */


// Coverage and the cancellation count are this professional's treatment of the
// patient (migration 0040), not the person's with everyone they see.
const coverageLabel = (treatment) => {
  const plan = treatment?.insurance_plans;
  if (!plan) return t('insurance.particular');
  return `${plan.insurance_companies?.name ?? ''} · ${plan.name}`;
};

const AppointmentRow = ({ appointment, durations, onAction, busyId }) => {
  const dependent = appointment.dependents;
  const treatment = appointment.treatments;
  const duration = durations[appointment.id];
  const busy = busyId === appointment.id;
  const age = ageAt(dependent?.date_of_birth, appointment.starts_at);

  const actions = [];
  if (appointment.status === STATUS.SCHEDULED) {
    actions.push(
      { key: 'waiting', label: t('agenda.markArrived'), icon: LogIn, variant: 'secondary' },
      { key: 'in_session', label: t('agenda.startSession'), icon: Play, variant: 'primary' }
    );
  }
  if (appointment.status === STATUS.WAITING) {
    actions.push({ key: 'in_session', label: t('agenda.startSession'), icon: Play, variant: 'primary' });
  }
  if (appointment.status === STATUS.IN_SESSION) {
    actions.push({ key: 'completed', label: t('agenda.endSession'), icon: Square, variant: 'success' });
  }
  if ([STATUS.SCHEDULED, STATUS.WAITING].includes(appointment.status)) {
    actions.push(
      // Someone phoning to move today's session lands here, not in their file.
      { key: 'reschedule', label: t('appointments.reschedule'), icon: CalendarClock, variant: 'secondary' },
      { key: 'no_show', label: t('appointments.status.no_show'), icon: UserX, variant: 'secondary' },
      { key: 'cancelled', label: t('common.cancel'), icon: XCircle, variant: 'secondary' }
    );
  }

  return (
    <li className="rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-slate-900">
              {formatTime(appointment.starts_at)}–{formatTime(appointment.ends_at)}
            </span>
            <span className="font-semibold text-slate-900">
              {dependent?.first_name} {dependent?.last_name}
            </span>
            {age != null && (
              <span className="text-xs text-slate-500">{t('clinicalHistory.yearsOld', { age })}</span>
            )}
            <Badge tone={STATUS_TONE[appointment.status]}>
              {t(`appointments.status.${appointment.status}`)}
            </Badge>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span>{coverageLabel(treatment)}</span>
            <span className="flex items-center gap-1">
              <Wallet size={12} />{formatMoney(appointment.patient_amount)}
              {appointment.patient_payment_status === 'paid' && (
                <Badge tone="success">{t('payments.status.paid')}</Badge>
              )}
              {appointment.patient_payment_status === 'partial' && (
                <Badge tone="warning">{t('payments.status.partial')}</Badge>
              )}
              {appointment.patient_payment_status === 'waived' && (
                <Badge tone="neutral">{t('payments.status.waived')}</Badge>
              )}
            </span>
            {appointment.locations && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />{appointment.locations.name}
              </span>
            )}
          </div>

          {(duration?.waiting_minutes != null || duration?.session_minutes != null) && (
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
              {duration.waiting_minutes != null && (
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {t('checkIn.waitingTime')}: {formatDuration(Math.round(duration.waiting_minutes))}
                </span>
              )}
              {duration.session_minutes != null && (
                <span>
                  {t('checkIn.sessionDuration')}: {formatDuration(Math.round(duration.session_minutes))}
                </span>
              )}
            </div>
          )}

          {treatment?.consecutive_cancellations >= 3 && (
            <p className="mt-2 flex items-center gap-1 text-xs font-medium text-amber-700">
              <AlertTriangle size={12} />
              {t('appointments.cancellationWarning', { count: treatment.consecutive_cancellations })}
            </p>
          )}

          {appointment.status === STATUS.CANCELLED && appointment.cancellation_reason && (
            <p className="mt-2 text-xs italic text-slate-500">“{appointment.cancellation_reason}”</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant}
              loading={busy}
              onClick={() => onAction(appointment, action.key)}
              className="text-xs"
            >
              <action.icon size={14} />{action.label}
            </Button>
          ))}

          {/* A request is answered in Solicitudes, not moved from here (0055). */}
          {appointment.status === STATUS.REQUESTED && (
            <Link to="/solicitudes">
              <Button variant="secondary" className="text-xs">
                <ClipboardCheck size={14} />{t('appointments.answerRequest')}
              </Button>
            </Link>
          )}

          {[STATUS.CANCELLED, STATUS.NO_SHOW].includes(appointment.status) && (
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={appointment.is_billable}
                onChange={(event) => onAction(appointment, 'billable', event.target.checked)}
              />
              {t('agenda.chargeAnyway')}
            </label>
          )}
        </div>
      </div>
    </li>
  );
};

const Section = ({ title, tone, appointments, ...rest }) => {
  if (!appointments.length) return null;
  return (
    <div>
      <h2 className={`mb-2 text-sm font-semibold uppercase tracking-wide ${tone}`}>
        {title} <span className="text-slate-400">({appointments.length})</span>
      </h2>
      <ul className="space-y-3">
        {appointments.map((appointment) => (
          <AppointmentRow key={appointment.id} appointment={appointment} {...rest} />
        ))}
      </ul>
    </div>
  );
};

const AttendanceBar = ({ therapistId }) => {
  const [open, setOpen] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: attendance }, { data: locationRows }] = await Promise.all([
      fetchOpenAttendance(therapistId),
      supabase.from('locations').select('id,name,is_online').eq('is_active', true).order('name'),
    ]);
    setOpen(attendance ?? null);
    setLocations(locationRows ?? []);
    if (locationRows?.length && !locationId) setLocationId(locationRows[0].id);
  }, [therapistId, locationId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    if (open) await checkOutProfessional(open.id);
    else await checkInProfessional({ therapistId, locationId });
    setBusy(false);
    load();
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2 text-sm">
        <Clock size={16} className="text-slate-400" />
        {open ? (
          <span className="text-slate-700">
            {t('agenda.inOfficeSince', { time: formatTime(open.checked_in_at) })}
            {open.locations?.name && ` · ${open.locations.name}`}
          </span>
        ) : (
          <span className="text-slate-500">{t('agenda.notCheckedIn')}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!open && locations.length > 0 && (
          <Select value={locationId} onChange={(event) => setLocationId(event.target.value)} className="w-44">
            {locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </Select>
        )}
        <Button variant={open ? 'secondary' : 'primary'} loading={busy} onClick={toggle}>
          {open ? <><LogOut size={14} />{t('checkIn.professionalCheckOut')}</>
                : <><LogIn size={14} />{t('checkIn.professionalCheckIn')}</>}
        </Button>
      </div>
    </div>
  );
};

const AgendaView = () => {
  // Every professional's own day, admin's included: another professional's
  // patients are not admin's to see (migration 0038).
  const { therapistId } = useSession();
  // A day key on the practice's calendar (lib/practiceTime.js), so "today" and
  // the day's sessions are the practice's day whatever clock the browser is on.
  const [date, setDate] = useState(() => dayKey());
  const [appointments, setAppointments] = useState([]);
  const [durations, setDurations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [appointmentResult, durationResult] = await Promise.all([
        fetchDayAppointments({ date, therapistId }),
        fetchDayDurations({ date, therapistId }),
      ]);
      if (appointmentResult.error) throw appointmentResult.error;
      setAppointments(appointmentResult.data ?? []);
      setDurations(Object.fromEntries((durationResult.data ?? []).map((row) => [row.appointment_id, row])));
    } catch (loadError) {
      setError(loadError.message ?? String(loadError));
    } finally {
      setLoading(false);
    }
  }, [date, therapistId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (appointment, action, value) => {
    if (action === 'cancelled') { setCancelTarget(appointment); return; }
    if (action === 'reschedule') { setMoveTarget(appointment); return; }

    setBusyId(appointment.id);
    if (action === 'billable') {
      const { error: billableError } = await setBillable(appointment.id, value);
      if (billableError) setError(billableError.message);
    } else {
      // 'waiting' set from here is the manual fallback for a patient who
      // arrived without scanning the QR.
      const { error: actionError } = await changeStatus({
        appointmentId: appointment.id,
        toStatus: action,
        source: 'professional',
      });
      if (actionError) setError(actionError.message);
    }
    setBusyId(null);
    load();
  };

  const groups = useMemo(() => groupDay(appointments), [appointments]);
  const shiftDay = (offset) => setDate(addDaysToKey(date, offset));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('nav.agenda')}</h1>
          {/* first-letter, not `capitalize`: Spanish writes "sábado, 5 de
              septiembre", so uppercasing every word would be wrong */}
          <p className="text-sm text-slate-500 first-letter:uppercase">
            {formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => shiftDay(-1)}><ChevronLeft size={16} /></Button>
          <Button
            variant={date === dayKey() ? 'primary' : 'secondary'}
            onClick={() => setDate(dayKey())}
          >
            {t('agenda.today')}
          </Button>
          <Button variant="secondary" onClick={() => shiftDay(1)}><ChevronRight size={16} /></Button>
        </div>
      </div>

      <AttendanceBar therapistId={therapistId} />

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {loading ? <Spinner /> : appointments.length === 0 ? (
        <Card><EmptyState icon={CalendarDays} title={t('appointments.noAppointmentsToday')} /></Card>
      ) : (
        <Card>
          <div className="space-y-6">
            <Section
              title={t('agenda.inSession')} tone="text-indigo-700"
              appointments={groups.current}
              durations={durations} onAction={handleAction} busyId={busyId}
            />
            <Section
              title={t('agenda.waitingRoom')} tone="text-amber-700"
              appointments={groups.waiting}
              durations={durations} onAction={handleAction} busyId={busyId}
            />
            <Section
              title={t('appointments.upcoming')} tone="text-slate-600"
              appointments={groups.upcoming}
              durations={durations} onAction={handleAction} busyId={busyId}
            />
            <Section
              title={t('agenda.finished')} tone="text-slate-400"
              appointments={groups.previous}
              durations={durations} onAction={handleAction} busyId={busyId}
            />
          </div>
        </Card>
      )}

      {/* Who asked to cancel, when they gave notice, and whether it is charged
          (0052). The same dialog as a patient's upcoming sessions in Pacientes. */}
      {cancelTarget && (
        <CancelSessionModal
          appointment={cancelTarget}
          patientName={`${cancelTarget.dependents?.first_name ?? ''} ${cancelTarget.dependents?.last_name ?? ''}`.trim()}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); load(); }}
        />
      )}

      {moveTarget && (
        <RescheduleModal
          appointment={moveTarget}
          therapistId={moveTarget.therapists?.id ?? therapistId}
          patientName={`${moveTarget.dependents?.first_name} ${moveTarget.dependents?.last_name}`}
          onClose={() => setMoveTarget(null)}
          onDone={() => { setNotice(t('reschedule.done')); load(); }}
        />
      )}
    </div>
  );
};

export default AgendaView;
